
import { cn } from "@/lib/utils";
import React, { forwardRef } from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  className?: string;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = "primary",
  size = "md",
  className,
  icon,
  iconPosition = "left",
  ...props
}, ref) => {
  const variants = {
    primary: "bg-tech-orange text-white hover:bg-orange-600 border-transparent",
    secondary: "bg-tech-cyan text-white hover:bg-cyan-600 border-transparent",
    outline: "bg-transparent border-tech-blue text-tech-blue hover:bg-tech-blue/5",
    ghost: "bg-transparent text-tech-blue hover:bg-tech-blue/5 border-transparent",
  };

  const sizes = {
    sm: "py-1.5 px-3 text-sm",
    md: "py-2.5 px-5 text-base",
    lg: "py-3 px-6 text-lg",
  };

  const iconSpacing = {
    left: icon ? "flex items-center gap-2" : "",
    right: icon ? "flex items-center gap-2 flex-row-reverse" : "",
  };

  return (
    <button
      ref={ref}
      className={cn(
        "font-medium rounded-md border-2 transition-all duration-300 font-['Montserrat'] shadow-sm hover:shadow-md",
        variants[variant],
        sizes[size],
        iconSpacing[iconPosition],
        className
      )}
      {...props}
    >
      {icon && icon}
      <span>{children}</span>
    </button>
  );
});

Button.displayName = "Button";

export default Button;
